import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Button,
  Typography,
  Box,
  CircularProgress,
  makeStyles,
  Tooltip,
  InputAdornment,
  Card,
  CardContent,
  Divider
} from '@material-ui/core';
import {
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Build as BuildIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as ArrowBackIcon
} from '@material-ui/icons';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import WorkorderDetailModal from './WorkorderDetailModal';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  filters: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: theme.shape.borderRadius,
  },
  filterControl: {
    minWidth: 150,
  },
  table: {
    minWidth: 650,
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    '& .MuiTableCell-head': {
      color: theme.palette.primary.contrastText,
      fontWeight: 'bold',
    },
  },
  priorityChip: {
    fontWeight: 'bold',
  },
  statusChip: {
    fontWeight: 'bold',
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  statsCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4],
    },
  },
  statNumber: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    lineHeight: 1,
  },
  statLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const WorkordersPage = ({ url, user, entity }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [workorders, setWorkorders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('scheduledDate');
  const [selectedWorkorder, setSelectedWorkorder] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [crews, setCrews] = useState([]);

  // Determine view mode based on route
  const viewMode = location.pathname === '/recent-checks' ? 'recent'
                 : location.pathname === '/upcoming-checks' ? 'upcoming'
                 : 'all';
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    completed: 0,
    scheduled: 0,
  });
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    crew: '',
    searchTerm: '',
    scheduledDateFrom: '',
    scheduledDateTo: '',
    createdDateFrom: '',
    createdDateTo: '',
    completeDateFrom: '',
    completeDateTo: '',
  });

  const priorityColors = {
    High: 'error',
    Medium: 'warning',
    Low: 'success',
  };

  const statusColors = {
    Open: 'warning',
    'In Progress': 'primary',
    Scheduled: 'info',
    Completed: 'success',
  };

  useEffect(() => {
    fetchWorkorders();
  }, [page, rowsPerPage, viewMode]);

  useEffect(() => {
    // Apply filters with debounce for search
    const timer = setTimeout(() => {
      if (page === 0) {
        fetchWorkorders();
      } else {
        setPage(0);
      }
    }, filters.searchTerm ? 500 : 0);
    return () => clearTimeout(timer);
  }, [filters]);

  const fetchWorkorders = async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${url}/api/hmi/genericGetRequest?type=ALL_IRRIGATION_WORKORDERS`);

      if (response?.data?.success) {
        let data = response.data.data || [];

        // Extract unique crews for filter dropdown
        const uniqueCrews = [];
        const crewMap = new Map();
        data.forEach(wo => {
          if (wo.crew && wo.crew._id && !crewMap.has(wo.crew._id)) {
            crewMap.set(wo.crew._id, wo.crew);
            uniqueCrews.push(wo.crew);
          }
        });
        setCrews(uniqueCrews.sort((a, b) => (a.name || '').localeCompare(b.name || '')));

        // Apply view mode filters first
        if (viewMode === 'recent') {
          // Recent checks = completed workorders from the last 30 days
          data = data.filter(wo =>
            wo.workComplete === true &&
            wo.work_complete_at &&
            moment().diff(moment(wo.work_complete_at), 'days') <= 30
          );
        } else if (viewMode === 'upcoming') {
          // Upcoming checks = non-completed workorders scheduled in the future
          data = data.filter(wo =>
            wo.workComplete !== true &&
            wo.scheduledDate &&
            moment(wo.scheduledDate).isAfter(moment().subtract(1, 'day'))
          );
        }

        // Apply user filters
        if (filters.status) {
          data = data.filter(wo => wo.status === filters.status);
        }
        if (filters.crew) {
          data = data.filter(wo => wo.crew?._id === filters.crew);
        }
        if (filters.searchTerm) {
          const searchLower = filters.searchTerm.toLowerCase();
          data = data.filter(wo =>
            wo.jobSite?.name?.toLowerCase().includes(searchLower) ||
            wo.description?.toLowerCase().includes(searchLower) ||
            wo.type?.toLowerCase().includes(searchLower)
          );
        }
        
        // Scheduled date filters
        if (filters.scheduledDateFrom) {
          data = data.filter(wo => moment(wo.scheduledDate).isSameOrAfter(moment(filters.scheduledDateFrom), 'day'));
        }
        if (filters.scheduledDateTo) {
          data = data.filter(wo => moment(wo.scheduledDate).isSameOrBefore(moment(filters.scheduledDateTo), 'day'));
        }
        
        // Created date filters
        if (filters.createdDateFrom) {
          data = data.filter(wo => moment(wo.createdDate || wo.createdAt).isSameOrAfter(moment(filters.createdDateFrom), 'day'));
        }
        if (filters.createdDateTo) {
          data = data.filter(wo => moment(wo.createdDate || wo.createdAt).isSameOrBefore(moment(filters.createdDateTo), 'day'));
        }
        
        // Complete date filters
        if (filters.completeDateFrom) {
          data = data.filter(wo => wo.work_complete_at && moment(wo.work_complete_at).isSameOrAfter(moment(filters.completeDateFrom), 'day'));
        }
        if (filters.completeDateTo) {
          data = data.filter(wo => wo.work_complete_at && moment(wo.work_complete_at).isSameOrBefore(moment(filters.completeDateTo), 'day'));
        }
        
        // Calculate stats
        const statsData = {
          total: data.length,
          open: data.filter(wo => wo.status === 'Open').length,
          inProgress: data.filter(wo => wo.status === 'In Progress').length,
          completed: data.filter(wo => wo.workComplete === true).length,
          scheduled: data.filter(wo => wo.status === 'Scheduled').length,
        };
        setStats(statsData);
        
        setTotalCount(data.length);
        setWorkorders(data);
      }
    } catch (error) {
      console.error('Error fetching workorders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters({
      ...filters,
      [field]: event.target.value,
    });
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      crew: '',
      searchTerm: '',
      scheduledDateFrom: '',
      scheduledDateTo: '',
      createdDateFrom: '',
      createdDateTo: '',
      completeDateFrom: '',
      completeDateTo: '',
    });
  };

  const handleViewDetails = (workorder) => {
    setSelectedWorkorder(workorder);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedWorkorder(null);
  };

  const getCrewDisplay = (crew) => {
    if (!crew) return 'Unassigned';
    const { name, foremanName } = crew;
    return `${name || ''}${foremanName ? ` (${foremanName})` : ''}`.trim() || 'Unassigned';
  };

  const handleExport = async () => {
    try {
      const response = await axios.post(`${url}/api/hmi/exportWorkorders`, {
        workorders: workorders,
      }, { responseType: 'blob' });
      
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `irrigation_workorders_${moment().format('YYYY-MM-DD')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting workorders:', error);
    }
  };

  const headCells = [
    { id: 'jobSite', label: 'Property', sortable: true },
    { id: 'lastIrrigationCheck', label: 'Last Check', sortable: true },
    { id: 'status', label: 'Status', sortable: true },
    { id: 'crew', label: 'Assigned To', sortable: false },
    { id: 'created_at', label: 'Created', sortable: true },
    { id: 'scheduledDate', label: 'Scheduled', sortable: true },
    { id: 'work_complete_at', label: 'Completed', sortable: true },
    { id: 'actions', label: 'Actions', sortable: false },
  ];

  const sortedWorkorders = stableSort(workorders, getComparator(order, orderBy));
  const paginatedWorkorders = sortedWorkorders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className={classes.root}>
      {/* Back to Dashboard Button */}
      {(viewMode === 'recent' || viewMode === 'upcoming') && (
        <Box mb={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push('/hmiirrigationdashboard')}
            color="primary"
          >
            Back to Dashboard
          </Button>
        </Box>
      )}

      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.title}>
          {viewMode === 'recent' ? (
            <CheckCircleIcon color="primary" fontSize="large" />
          ) : viewMode === 'upcoming' ? (
            <EventIcon color="primary" fontSize="large" />
          ) : (
            <BuildIcon color="primary" fontSize="large" />
          )}
          <Typography variant="h4">
            {viewMode === 'recent' ? 'Recent Irrigation Checks (Last 30 Days)'
             : viewMode === 'upcoming' ? 'Upcoming Irrigation Checks'
             : 'Irrigation Work Orders (2025)'}
          </Typography>
        </Box>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            style={{ marginRight: 8 }}
          >
            {showFilters ? 'Hide' : 'Show'} Filters
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<RefreshIcon />}
            onClick={fetchWorkorders}
            style={{ marginRight: 8 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ExportIcon />}
            onClick={handleExport}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent className={classes.statsCard}>
              <Typography className={classes.statNumber} style={{ color: '#333' }}>
                {stats.total}
              </Typography>
              <Typography className={classes.statLabel}>Total</Typography>
            </CardContent>
          </Card>
        </Grid>
        {/* <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent className={classes.statsCard}>
              <Typography className={classes.statNumber} style={{ color: '#ff9800' }}>
                {stats.open}
              </Typography>
              <Typography className={classes.statLabel}>Open</Typography>
            </CardContent>
          </Card>
        </Grid> */}
        {/* <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent className={classes.statsCard}>
              <Typography className={classes.statNumber} style={{ color: '#2196f3' }}>
                {stats.inProgress}
              </Typography>
              <Typography className={classes.statLabel}>In Progress</Typography>
            </CardContent>
          </Card>
        </Grid> */}
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent className={classes.statsCard}>
              <Typography className={classes.statNumber} style={{ color: '#4caf50' }}>
                {stats.completed}
              </Typography>
              <Typography className={classes.statLabel}>Completed</Typography>
            </CardContent>
          </Card>
        </Grid>
        {/* <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent className={classes.statsCard}>
              <Typography className={classes.statNumber} style={{ color: '#17a2b8' }}>
                {stats.scheduled}
              </Typography>
              <Typography className={classes.statLabel}>Scheduled</Typography>
            </CardContent>
          </Card>
        </Grid> */}
      </Grid>

      {/* Filters */}
      {showFilters && (
        <Paper className={classes.filters}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search"
                variant="outlined"
                size="small"
                value={filters.searchTerm}
                onChange={handleFilterChange('searchTerm')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                placeholder="Search property, type, description..."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={handleFilterChange('status')}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Open">Open</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="Scheduled">Scheduled</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Assigned Crew</InputLabel>
                <Select
                  value={filters.crew}
                  onChange={handleFilterChange('crew')}
                  label="Assigned Crew"
                >
                  <MenuItem value="">All Crews</MenuItem>
                  {crews.map((crew) => (
                    <MenuItem key={crew._id} value={crew._id}>
                      {crew.name || 'Unnamed'}{crew.foremanName ? ` (${crew.foremanName})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
              >
                Clear All
              </Button>
            </Grid>
            
            {/* Row 2 - Date Filters */}
            <Grid item xs={12}>
              <Divider style={{ margin: '8px 0' }} />
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Date Filters
              </Typography>
            </Grid>
            
            {/* Scheduled Dates */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Scheduled From"
                type="date"
                variant="outlined"
                size="small"
                value={filters.scheduledDateFrom}
                onChange={handleFilterChange('scheduledDateFrom')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Scheduled To"
                type="date"
                variant="outlined"
                size="small"
                value={filters.scheduledDateTo}
                onChange={handleFilterChange('scheduledDateTo')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            {/* Created Dates */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Created From"
                type="date"
                variant="outlined"
                size="small"
                value={filters.createdDateFrom}
                onChange={handleFilterChange('createdDateFrom')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Created To"
                type="date"
                variant="outlined"
                size="small"
                value={filters.createdDateTo}
                onChange={handleFilterChange('createdDateTo')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            {/* Complete Dates */}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Completed From"
                type="date"
                variant="outlined"
                size="small"
                value={filters.completeDateFrom}
                onChange={handleFilterChange('completeDateFrom')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Completed To"
                type="date"
                variant="outlined"
                size="small"
                value={filters.completeDateTo}
                onChange={handleFilterChange('completeDateTo')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Quick Search Bar */}
      <Paper style={{ padding: 16, marginBottom: 16 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Quick search by property name, workorder number, crew, or description..."
          value={filters.searchTerm}
          onChange={handleFilterChange('searchTerm')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: filters.searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setFilters({ ...filters, searchTerm: '' })}
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Table */}
      <Paper>
        {loading ? (
          <Box className={classes.loadingWrapper}>
            <CircularProgress />
          </Box>
        ) : workorders.length === 0 ? (
          <Box className={classes.emptyState}>
            <AssignmentIcon style={{ fontSize: 64, marginBottom: 16 }} />
            <Typography variant="h6">No work orders found</Typography>
            <Typography variant="body2">
              {Object.values(filters).some(f => f) 
                ? 'Try adjusting your filters' 
                : 'No work orders available'}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table className={classes.table}>
                <TableHead className={classes.tableHeader}>
                  <TableRow>
                    {headCells.map((headCell) => (
                      <TableCell
                        key={headCell.id}
                        sortDirection={orderBy === headCell.id ? order : false}
                      >
                        {headCell.sortable ? (
                          <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={(event) => handleRequestSort(event, headCell.id)}
                            style={{ color: 'inherit' }}
                          >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                              <span className={classes.visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                              </span>
                            ) : null}
                          </TableSortLabel>
                        ) : (
                          headCell.label
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedWorkorders.map((workorder) => (
                    <TableRow key={workorder._id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {workorder.jobSite?.name || 'Unknown'}
                            {workorder.number && (
                              <Chip
                                label={`WO #${workorder.number}`}
                                size="small"
                                style={{ marginLeft: 8, fontSize: '0.7rem' }}
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Typography>
                          {workorder.jobSite?.address && (
                            <Typography variant="caption" color="textSecondary">
                              {workorder.jobSite.address}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {workorder.jobSite?.lastIrrigationCheck ? (
                          <Box>
                            <Typography variant="body2">
                              {moment(workorder.jobSite.lastIrrigationCheck).format('MMM DD')}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              ({moment().diff(moment(workorder.jobSite.lastIrrigationCheck), 'days')} days)
                            </Typography>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={workorder.status || 'Open'}
                          size="small"
                          color={statusColors[workorder.status] || 'default'}
                          className={classes.statusChip}
                        />
                      </TableCell>
                      <TableCell>{getCrewDisplay(workorder.crew)}</TableCell>
                      <TableCell>
                        {workorder.created_at || workorder.createdAt
                          ? moment(workorder.created_at || workorder.createdAt).format('MMM DD')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {workorder.scheduledDate 
                          ? moment(workorder.scheduledDate).format('M/D/Y')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {workorder.work_complete_at
                          ? moment(workorder.work_complete_at).format('MMM DD')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Box className={classes.actionButtons}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewDetails(workorder)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          {workorder.job && workorder.client && (
                            <Tooltip title="Open Job">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => {
                                  window.open(`/clients/${workorder.client}/Job/${workorder.job}`, '_blank');
                                }}
                              >
                                <LocationIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100, 500]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Detail Modal */}
      <WorkorderDetailModal
        open={modalOpen}
        workorder={selectedWorkorder}
        onClose={handleCloseModal}
        url={url}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  entity: state.entity,
});

export default connect(mapStateToProps)(WorkordersPage);